import type { MarketId, TemplateView } from '@ps/isp-web-services-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';

import GroupInPanel from '../GroupInPanel/GroupInPanel';

import styles from './MarketTemplatePanel.scss';
import TemplatePanelTitle from './TemplatePanelTitle';

interface Props {
  competitionGroup: TemplateView[][];
  marketId: MarketId;
  groupedOn: 'DATE' | 'COMPETITION';
  isNested: boolean;
  isFlush?: boolean;
}

const MarketTemplatePanel: React.FC<Props> = ({
  competitionGroup,
  groupedOn,
  marketId,
  isNested,
  isFlush = false
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : -1)
  );
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const panelName = groupedOn === 'DATE' ? event.eventStartTime : competition.competitionName;

  return (
    <div
      className={classNames(styles.panel, {
        [styles.nestedPanel]: isNested,
        [styles.flush]: isFlush
      })}
      key={panelName}
    >
      <ExpandablePanel
        isOpenInitially
        isNested={isNested}
        title={
          <TemplatePanelTitle
            groupedOn={groupedOn}
            competition={competition}
            isNested={isNested}
            panelName={panelName}
          />
        }
      >
        {competitionGroup.map((groupInPanel, i) => {
          const [{ template }] = groupInPanel;

          if (isCompoundMarketTemplate(template)) {
            const [mktId] = template.marketIds.filter(Boolean);

            return (
              <GroupInPanel
                key={`${marketId}-panel-${i}`}
                groupInPanel={groupInPanel}
                marketId={mktId ?? ''}
              />
            );
          }

          if (isSingleMarketTemplate(template)) {
            return (
              <GroupInPanel
                key={`${marketId}-panel-${i}`}
                groupInPanel={groupInPanel}
                marketId={template.marketId}
              />
            );
          }

          return (
            <GroupInPanel
              key={`${marketId}-panel-${i}`}
              groupInPanel={groupInPanel}
              marketId={marketId}
            />
          );
        })}
      </ExpandablePanel>
    </div>
  );
};

export default MarketTemplatePanel;
